import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { FieldLabel } from '../../shared/ui/field-label';
import { Hint } from '../../shared/ui/hint';
import { Input } from '../../shared/ui/input';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { Segmented } from '../../shared/ui/segmented';
import { upsertCustomerSecret, type TUpsertedCustomerSecret } from '../../entities/customer';
import type { TSourceAuthMethod } from '../../entities/source';
import { MethodField } from './ui/method-field';

type TOrigin = 'generate' | 'paste';
type TKeep = '24' | '48' | '168' | '0';

const ORIGINS = [
  { value: 'generate', label: 'Generate one' },
  { value: 'paste', label: 'Paste theirs' },
] as const;

const KEEP = [
  { value: '24', label: '24 h' },
  { value: '48', label: '2 d' },
  { value: '168', label: '7 d' },
  { value: '0', label: 'End it now' },
] as const;

interface ISecretDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `rotate` replaces a working secret and asks how long the old one keeps working. */
  mode: 'rotate' | 'add';
  initialOrigin: TOrigin;
  customerId: string;
  sourceId: string;
  clientName: string;
  /** The source's enabled methods that take a secret; a picker appears when there is more than one. */
  methods: readonly TSourceAuthMethod[];
  initialMethodId?: string;
  onDone: (result: TUpsertedCustomerSecret, generated: boolean) => void;
}

export const SecretDialog = ({
  open,
  onOpenChange,
  mode,
  initialOrigin,
  customerId,
  sourceId,
  clientName,
  methods,
  initialMethodId,
  onDone,
}: ISecretDialogProps) => {
  const [origin, setOrigin] = useState<TOrigin>(initialOrigin);
  const [value, setValue] = useState('');
  const [keep, setKeep] = useState<TKeep>('48');
  const [methodId, setMethodId] = useState(initialMethodId ?? methods[0]?.id ?? '');

  // Generated on the server, never here: it knows the prefix the sender's scheme insists on.
  const save = useMutation({
    mutationFn: () =>
      upsertCustomerSecret(customerId, sourceId, {
        ...(methodId ? { authMethodId: methodId } : {}),
        ...(origin === 'paste' ? { value: value.trim() } : {}),
        ...(mode === 'rotate' ? { graceHours: Number(keep) } : {}),
      }),
    onSuccess: (result) => {
      setValue('');
      onDone(result, origin === 'generate');
    },
  });

  const change = (next: boolean) => {
    if (!next) {
      setValue('');
      save.reset();
    }
    onOpenChange(next);
  };

  return (
    <Modal
      open={open}
      onOpenChange={change}
      title={mode === 'rotate' ? `Rotate ${clientName}’s secret` : `Add ${clientName}’s secret`}
      description="Their address stays the same."
      submit={{
        label: mode === 'rotate' ? 'Rotate' : 'Add it',
        onSubmit: () => save.mutate(),
        disabled: origin === 'paste' && value.trim().length === 0,
        waiting: save.isPending,
        waitingLabel: 'Saving',
      }}
    >
      <div className="flex flex-col gap-4">
        <MethodField
          id="secret-dialog-method"
          methods={methods}
          value={methodId}
          onChange={setMethodId}
        />
        <div>
          <FieldLabel>New secret</FieldLabel>
          <Segmented
            fill
            aria-label="New secret"
            segments={ORIGINS}
            value={origin}
            onChange={setOrigin}
          />
          {origin === 'paste' ? (
            <Input
              mono
              autoFocus
              className="mt-2"
              aria-label="Their secret"
              placeholder="the secret they sign with"
              value={value}
              onChange={(event) => setValue(event.target.value)}
            />
          ) : (
            <Hint>
              Made when you press {mode === 'rotate' ? 'Rotate' : 'Add it'} and shown once. Send it
              to {clientName}; they sign with it from now on.
            </Hint>
          )}
        </div>

        {mode === 'rotate' && (
          <div>
            <FieldLabel>Keep the old one working for</FieldLabel>
            <Segmented
              fill
              aria-label="Keep the old one working for"
              segments={KEEP}
              value={keep}
              onChange={setKeep}
            />
            <Hint>
              {keep === '0'
                ? 'The old one stops working the moment you rotate.'
                : 'Until then both are accepted.'}
            </Hint>
          </div>
        )}

        {save.isError && (
          <Note tone="danger">Not accepted: {save.error.message} Their secret is unchanged.</Note>
        )}
      </div>
    </Modal>
  );
};
